import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Heart, Users, Target, Award, Lightbulb, Shield, Globe, ArrowRight } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import WhatsAppButton from '../components/WhatsAppButton';

const AboutPage = () => {
  const heroRef = useRef(null);
  const storyRef = useRef(null);
  const teamRef = useRef(null);
  const valuesRef = useRef(null);
  
  const heroInView = useInView(heroRef, { once: true });
  const storyInView = useInView(storyRef, { once: true });
  const teamInView = useInView(teamRef, { once: true });
  const valuesInView = useInView(valuesRef, { once: true });

  const boardMembers = [
    {
      name: "Keshav Jha",
      position: "Chief Executive Officer",
      description: "Visionary leader driving SupaAid's mission to revolutionize emergency healthcare through AI technology.",
      avatar: "KJ"
    },
    {
      name: "Krishna Devi",
      position: "Director",
      description: "Strategic advisor with extensive experience in healthcare operations and organizational development.",
      avatar: "KD"
    },
    {
      name: "Vani Jha", 
      position: "Director",
      description: "Healthcare industry expert focused on regulatory compliance and sustainable business growth.",
      avatar: "VJ"
    }
  ];

  const values = [
    {
      icon: Heart,
      title: "Life First",
      description: "Every decision we make is driven by our commitment to saving lives and improving health outcomes."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "We leverage cutting-edge AI technology to solve complex healthcare challenges with simple solutions."
    },
    {
      icon: Shield,
      title: "Trust & Security",
      description: "Patient data security and privacy are paramount in everything we build and deploy."
    },
    {
      icon: Users,
      title: "Accessibility",
      description: "Healthcare should be accessible to everyone, regardless of location or economic status."
    },
    {
      icon: Target,
      title: "Precision",
      description: "Our AI-powered solutions deliver accurate, timely responses when every second counts."
    },
    {
      icon: Globe,
      title: "Impact",
      description: "We're building technology that creates measurable positive impact on global health outcomes."
    }
  ];

  const milestones = [
    {
      year: "2025",
      title: "SupaAid Founded",
      description: "Officially established and recognized as a startup by the Department for Promotion of Industry and Internal Trade"
    },
    {
      year: "2025",
      title: "AI Engine Launch",
      description: "Deployed our proprietary AI triage system with 93% faster response times"
    },
    {
      year: "2025",
      title: "Emergency Network",
      description: "Built comprehensive network of hospitals and blood banks for seamless coordination"
    },
    {
      year: "2025",
      title: "24/7 AI Doctor",
      description: "Launched consumer-facing AI doctor for round-the-clock medical guidance"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-blue-50/30">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-6 leading-tight">
              About <span className="text-blue-600">SupaAid</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              We're on a mission to save lives through AI-powered emergency healthcare technology, 
              making critical medical response faster, smarter, and more accessible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section ref={storyRef} className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={storyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            <div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-6">
                Our <span className="text-blue-600">Story</span>
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  SupaAid was born from a simple yet powerful realization: in medical emergencies, 
                  every second counts, but traditional healthcare systems often waste precious hours 
                  in coordination and communication.
                </p>
                <p>
                  We witnessed firsthand how a 4-hour search for blood banks could be reduced to 
                  6 minutes with the right technology. This sparked our mission to build AI-powered 
                  solutions that eliminate inefficiencies in emergency healthcare.
                </p>
                <p>
                  Today, as a DPIIT-recognized startup, we're revolutionizing how hospitals, 
                  blood banks, and patients connect during critical moments, ensuring that 
                  help arrives when it's needed most.
                </p>
              </div>
            </div>
            
            <motion.div
              className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-3xl p-8 sm:p-12"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-4xl sm:text-5xl font-bold text-blue-600 mb-2">300K+</div>
                  <div className="text-gray-600 font-medium">Lives at Risk</div>
                  <div className="text-sm text-gray-500">Preventable deaths annually</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl sm:text-5xl font-bold text-blue-600 mb-2">93%</div>
                  <div className="text-gray-600 font-medium">Faster Response</div>
                  <div className="text-sm text-gray-500">4hrs → 6min with AI</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl sm:text-5xl font-bold text-blue-600 mb-2">24/7</div>
                  <div className="text-gray-600 font-medium">AI Availability</div>
                  <div className="text-sm text-gray-500">Round-the-clock care</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl sm:text-5xl font-bold text-blue-600 mb-2">₹269M</div>
                  <div className="text-gray-600 font-medium">Market Size</div>
                  <div className="text-sm text-gray-500">By 2028 projection</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50/50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl border border-gray-100"
            >
              <div className="bg-blue-500/10 rounded-2xl p-4 w-fit mb-6">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-3xl sm:text-4xl font-bold text-black mb-4">Our Mission</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                To save lives by revolutionizing emergency healthcare through AI-powered technology, 
                ensuring that critical medical resources reach patients faster and more efficiently 
                than ever before.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 sm:p-12 text-white shadow-xl"
            >
              <div className="bg-white/20 rounded-2xl p-4 w-fit mb-6">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl sm:text-4xl font-bold mb-4">Our Vision</h3>
              <p className="text-lg text-blue-50 leading-relaxed">
                To create a world where medical emergencies are met with instant, intelligent responses, 
                where geography and time are no longer barriers to receiving life-saving care.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Board of Directors */}
      <section ref={teamRef} className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={teamInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-6">
              Board of <span className="text-blue-600">Directors</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the visionary leaders driving SupaAid's mission to transform emergency healthcare
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {boardMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={teamInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 shadow-xl border border-gray-100 text-center"
              >
                <div className="bg-blue-600 text-white rounded-full w-20 h-20 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  {member.avatar}
                </div>
                <h3 className="text-2xl font-bold text-black mb-2">{member.name}</h3>
                <div className="text-blue-600 font-semibold mb-4">{member.position}</div>
                <p className="text-gray-600 leading-relaxed">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section ref={valuesRef} className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50/30 via-white to-blue-50/20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={valuesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-6">
              Our <span className="text-blue-600">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at SupaAid
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
                >
                  <div className="bg-blue-500/10 rounded-xl p-3 w-fit mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Milestones Timeline */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-6">
              Our <span className="text-blue-600">Journey</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Key milestones in our mission to revolutionize emergency healthcare
            </p>
          </motion.div>

          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`flex items-center gap-8 ${index % 2 === 1 ? 'flex-row-reverse' : ''}`}
              >
                <div className="flex-1">
                  <div className={`bg-gradient-to-br ${index % 2 === 0 ? 'from-blue-50 to-white' : 'from-blue-600 to-blue-700 text-white'} rounded-2xl p-8 shadow-lg`}>
                    <div className={`text-sm font-semibold mb-2 ${index % 2 === 0 ? 'text-blue-600' : 'text-blue-200'}`}>
                      {milestone.year}
                    </div>
                    <h3 className={`text-2xl font-bold mb-3 ${index % 2 === 0 ? 'text-black' : 'text-white'}`}>
                      {milestone.title}
                    </h3>
                    <p className={`${index % 2 === 0 ? 'text-gray-600' : 'text-blue-100'} leading-relaxed`}>
                      {milestone.description}
                    </p>
                  </div>
                </div>
                <div className="w-4 h-4 bg-blue-600 rounded-full flex-shrink-0" />
                <div className="flex-1" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default AboutPage;